import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_META = {
  jacket: { label: "Jacket", color: "text-amber-300", border: "border-amber-500/40", hover: "hover:bg-amber-500/10" },
  pant:   { label: "Pant",   color: "text-sky-300",   border: "border-sky-500/40",   hover: "hover:bg-sky-500/10" },
  vest:   { label: "Vest",   color: "text-fuchsia-300", border: "border-fuchsia-500/40", hover: "hover:bg-fuchsia-500/10" },
  shirt:  { label: "Shirt",  color: "text-emerald-300", border: "border-emerald-500/40", hover: "hover:bg-emerald-500/10" },
  all:    { label: "Style",  color: "text-purple-300",  border: "border-purple-500/40",  hover: "hover:bg-purple-500/10" },
};

/**
 * "Load Style" button.
 *
 * When `section` is one of "jacket" | "pant" | "vest" | "shirt", only that
 * section's field map is applied (other sections are untouched) and the dialog
 * filters to presets that have data for that section.
 *
 * When `section` is omitted, behaves as a global loader that applies every
 * non-empty section of the chosen preset.
 */
export default function LoadStylePresetButton({
  section,              // "jacket" | "pant" | "vest" | "shirt" | undefined
  setJacketStyling,
  setPantStyling,
  setVestStyling,
  setShirtStyling,
  jacketStyling = {},
  pantStyling = {},
  vestStyling = {},
  shirtStyling = {},
  testId = "load-style-preset",
}) {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [applyingId, setApplyingId] = useState(null);

  const meta = SECTION_META[section || "all"];

  useEffect(() => {
    if (!open || presets.length) return;
    setLoading(true);
    axios
      .get(`${API}/style-presets`)
      .then((res) => setPresets(res.data?.rows || []))
      .catch(() => toast.error("Failed to load style presets"))
      .finally(() => setLoading(false));
  }, [open, presets.length]);

  const filtered = useMemo(() => {
    let list = presets;
    // Filter to presets that have data for the requested section
    if (section) {
      list = list.filter((p) => (p.section_fabric || {})[section]);
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      return (
        (p.preset_id || "").toLowerCase().includes(q) ||
        (p.name || "").toLowerCase().includes(q) ||
        (p.tag || "").toLowerCase().includes(q) ||
        (p.jacket_name || "").toLowerCase().includes(q) ||
        (p.pant_name || "").toLowerCase().includes(q) ||
        (p.customer || "").toLowerCase().includes(q) ||
        (p.fabric_code || "").toLowerCase().includes(q)
      );
    });
  }, [search, presets, section]);

  const applyPreset = async (preset_id) => {
    try {
      setApplyingId(preset_id);
      const res = await axios.get(`${API}/style-presets/${preset_id}`);
      const p = res.data;
      const applied = [];
      const tryApply = (sec, current, setter) => {
        if (!setter) return;
        const next = p[sec] || {};
        if (!Object.keys(next).length) return;
        setter({ ...current, ...next });
        applied.push(`${sec} (${Object.keys(next).length} fields)`);
      };
      if (section) {
        if (section === "jacket") tryApply("jacket", jacketStyling, setJacketStyling);
        if (section === "pant")   tryApply("pant",   pantStyling,   setPantStyling);
        if (section === "vest")   tryApply("vest",   vestStyling,   setVestStyling);
        if (section === "shirt")  tryApply("shirt",  shirtStyling,  setShirtStyling);
      } else {
        tryApply("jacket", jacketStyling, setJacketStyling);
        tryApply("pant",   pantStyling,   setPantStyling);
        tryApply("vest",   vestStyling,   setVestStyling);
        tryApply("shirt",  shirtStyling,  setShirtStyling);
      }
      toast.success(`Loaded ${preset_id}`, {
        description: applied.length
          ? applied.join(" · ")
          : (section ? `No ${section} data in this order` : "No matching fields"),
      });
      if (applied.length) setOpen(false);
    } catch (e) {
      toast.error("Failed to apply preset");
    } finally {
      setApplyingId(null);
    }
  };

  const sectionDots = (p) => {
    const sections = Object.keys(p.section_fabric || {});
    const dot = (s, color) =>
      sections.includes(s) ? (
        <span key={s} className={`text-[9px] uppercase font-mono px-1 py-0.5 rounded ${color}`}>
          {s[0]}
        </span>
      ) : null;
    return (
      <span className="flex gap-1 items-center">
        {dot("jacket", "bg-amber-500/20 text-amber-300")}
        {dot("pant", "bg-sky-500/20 text-sky-300")}
        {dot("vest", "bg-fuchsia-500/20 text-fuchsia-300")}
        {dot("shirt", "bg-emerald-500/20 text-emerald-300")}
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={`h-7 text-[11px] gap-1 ${meta.border} ${meta.color} ${meta.hover}`}
          data-testid={`${testId}-open-btn`}
        >
          <Download size={12} /> Load {meta.label} Style
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-[#0a1628] border-white/10" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className={meta.color}>
            Load {meta.label} Style from Historical Order
          </DialogTitle>
          <p className="text-xs text-slate-400">
            {section
              ? `Showing orders that have ${meta.label.toLowerCase()} styling. Only the ${meta.label.toLowerCase()} fields will be merged — other garments stay as-is.`
              : "Picking an order will merge its jacket / pant / vest / shirt fields into the current form."}
          </p>
        </DialogHeader>
        <div className="flex items-center gap-2 pb-2 border-b border-white/5">
          <Search size={14} className="text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order#, tag, customer, or fabric code"
            className="bg-transparent border-0 h-8 text-xs focus-visible:ring-0 px-0"
            data-testid={`${testId}-search`}
            autoFocus
          />
          <span className="text-[10px] text-slate-500">{filtered.length} order{filtered.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading presets…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <p className="text-center py-8 text-slate-500 text-sm">
              No presets match your search.
            </p>
          )}
          {!loading &&
            filtered.map((p) => {
              // Prefer section-specific name if loading a single section
              let displayName = p.preset_id;
              if (section === "jacket" && p.jacket_name) displayName = p.jacket_name;
              else if (section === "pant" && p.pant_name) displayName = p.pant_name;
              else if (p.tag) displayName = p.tag;
              return (
              <button
                key={p.preset_id}
                type="button"
                onClick={() => applyPreset(p.preset_id)}
                disabled={applyingId === p.preset_id}
                className={`w-full text-left px-3 py-2 border-b border-white/5 ${meta.hover} transition-colors flex items-center gap-3 disabled:opacity-50`}
                data-testid={`${testId}-row-${p.preset_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-xs text-slate-100 truncate">{displayName}</span>
                    <span className="font-mono text-[10px] text-amber-400/70">{p.preset_id}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {p.customer || "—"} · fabric <code className="text-slate-400">{p.fabric_code || "—"}</code>
                  </div>
                </div>
                {sectionDots(p)}
                {applyingId === p.preset_id && (
                  <Loader2 size={12} className="animate-spin text-purple-400" />
                )}
              </button>
              );
            })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
